import { Group, Stack } from '@mantine/core';
import { useForm, UseFormReturnType } from '@mantine/form';
import { closeModal, openModal } from '@mantine/modals';
import { Editor } from '@tiptap/react';
import Toast from 'components/Toast';
import FileListConfigurator from 'pages/revisions-module/template-editor/editors/template/extensions/placeholder/configurator/FileListConfigurator';
import ImageConfigurator from 'pages/revisions-module/template-editor/editors/template/extensions/placeholder/configurator/ImageConfigurator';
import PredefinedListConfigurator from 'pages/revisions-module/template-editor/editors/template/extensions/placeholder/configurator/PredefinedListConfigurator';
import { IPlaceholderType } from 'pages/revisions-module/template-editor/editors/template/extensions/placeholder/Placeholder';
import { useCallback, useEffect, useMemo } from 'react';
import Button from 'components/Button';

const MODAL_ID = 'placeholder-configurator';
const EVENT_NAME = 'open-placeholder-configurator';

/**
 * The discriminator for the device faults placeholder form.
 */
function FormDiscriminator({ type, form }: { type: IPlaceholderType; form: UseFormReturnType<any> }) {
  switch (type) {
    case 'predefinedList':
      return <PredefinedListConfigurator form={form} />;

    case 'organizationLogo':
    case 'technicianOrganizationLogo':
    case 'assignerOrganizationLogo':
    case 'technicianStamp':
    case 'technicianSignature':
    case 'confirmationSignature':
      return <ImageConfigurator form={form} />;

    case 'fileList':
      return <FileListConfigurator form={form} />;

    default:
      return <Toast type="warning" message="Tento typ nepodporuje formáty." fullWidth withCloseButton={false} />;
  }
}

/**
 * The configurator for the device faults placeholder.
 */
function PlaceholderConfigurator({ type, editor }: { type: IPlaceholderType; editor: Editor }) {
  const initialValues = useMemo(() => {
    const { format } = editor.getAttributes('blockPlaceholder');
    return format;
  }, []);

  const form = useForm({ initialValues });

  const close = useCallback(() => closeModal(MODAL_ID), []);

  const submit = useCallback(
    (format: any) => {
      editor.chain().focus().setPlaceholderFormat(format).run();
      close();
    },
    [editor, close]
  );

  return (
    <form onSubmit={form.onSubmit(submit)}>
      <Stack spacing={24}>
        <FormDiscriminator type={type} form={form} />
        <Group pt={8} spacing={16} position="right">
          <Button type="button" onClick={close} variant="subtle-gray">
            Zrušiť
          </Button>
          <Button type="submit" variant="primary">
            Nastaviť
          </Button>
        </Group>
      </Stack>
    </form>
  );
}

/**
 * Hook used to register the configurator placeholders.
 */
export default function usePlaceholderConfigurator(editor: Editor | null) {
  const openConfigurator = useCallback(
    (e: CustomEvent<{ type: IPlaceholderType }>) => {
      if (editor) {
        openModal({
          modalId: MODAL_ID,
          title: 'Konfigurátor formátu',
          size: 500,
          centered: true,
          returnFocus: false,
          children: <PlaceholderConfigurator type={e.detail.type} editor={editor} />,
        });
      }
    },
    [editor]
  );

  useEffect(() => {
    window.addEventListener(EVENT_NAME, openConfigurator as EventListener);
    return () => window.removeEventListener(EVENT_NAME, openConfigurator as EventListener);
  }, [openConfigurator]);
}
